<template>
  <Form
    v-bind="getBindValue"
    ref="formElRef"
    :class="getFormClass"
    :model="formModel"
    @keydown.enter="handleKeyboardEnter"
  >
    <Row v-bind="getRow">
      <slot name="formHeader"></slot>
      <template v-for="schema in getSchema" :key="schema['_key'] || schema.field">
        <FormItem
          :formActionType="formActionType"
          :schema="schema"
          :formProps="getProps"
          :allDefaultValues="defaultValueRef"
          :formModel="formModel"
          :setFormModel="setFormModel"
          :canFocusFixed="isInModal"
        >
          <template v-for="item in Object.keys($slots)" #[item]="data">
            <slot :name="item" v-bind="data || {}"></slot>
          </template>
        </FormItem>
      </template>

      <FormAction v-bind="getProps" :actionSpan="Number(actionColOptions?.span) || 6">
        <template v-for="item in ['resetBefore', 'submitBefore']" #[item]="data">
          <slot :name="item" v-bind="data || {}"></slot>
        </template>
      </FormAction>
      <slot name="formFooter"></slot>
    </Row>

    <BasePopover
      v-if="showDropDown"
      placement="bottomRight"
      :record="formModel"
      :options="dropDownOptions"
    >
      <div
        v-once
        class="px-4px font-500 bottom-50% translate-y-50% absolute right-0 flex cursor-pointer items-center bg-white"
      >
        ...
        <span class="ml-4px text-[#01a887]">{{ t('g.26050001') }}</span>
        <MIcon
          prefix="g"
          color="#01a887"
          :size="20"
          icon="arrow-down"
          class="font-bold leading-none"
        />
      </div>
    </BasePopover>
  </Form>
</template>

<script lang="ts" setup>
  import { Form, type FormProps as AntFormProps, Row } from 'ant-design-vue';
  import { findLastIndex, get } from 'lodash-es';
  import {
    computed,
    nextTick,
    onMounted,
    reactive,
    ref,
    shallowRef,
    unref,
    useAttrs,
    watch,
  } from 'vue';
  import type { Ref } from 'vue';
  import {
    cloneDeep,
    dateUtil,
    deepMerge,
    isArray,
    isBoolean,
    isFunction,
    omit,
    replaceUrl,
  } from '@mts-fe/utils';
  import { BasePopover, DataListItem } from '@mts/components/Base';
  import { useModalContext } from '@mts/components/Modal';
  import { useDesign } from '@mts/hooks/web/useDesign';
  import { t } from '@mts/hooks/web/useI18n';
  import { concatText } from '@mts/utils/index.js';
  import FormAction from './components/FormAction.vue';
  import FormItem from './components/SimpleFormItem.vue';
  import useDropdown from './hooks/useDropdown';
  import { createFormContext } from './hooks/useFormContext';
  import { useFormEvents } from './hooks/useFormEvents';
  import { useFormFocus } from './hooks/useFormFocus';
  import { useFormValues } from './hooks/useFormValues';
  import type { FormActionType, FormProps, FormSchemaInner as FormSchema } from './types/form';
  import {
    dateItemType,
    getReadonlyComponent,
    IGNORE_PROPS_KEYS,
    itemIsUploadComponent,
  } from './helper';
  import { basicProps } from './props';

  defineOptions({ name: 'SimpleForm', inheritAttrs: false });

  const props = defineProps(basicProps);

  const emit = defineEmits([
    'reset',
    'submit',
    'register',
    'focus-change',
    'field-value-change',
    'change',
  ]);

  const attrs = useAttrs();

  const formModel = reactive({});
  const modalFn = useModalContext();
  const isInModal = computed(() => !!Object.keys(modalFn).length);

  const defaultValueRef = ref({});
  const isInitedDefaultRef = ref(false);
  const propsRef = ref<Partial<FormProps>>();
  const schemaRef = ref<FormSchema[] | null>(null);
  const formElRef = ref<FormActionType | null>(null);

  const { prefixCls } = useDesign('basic-form');

  // Get the basic configuration of the form
  const getProps = computed(() => {
    return { ...props, ...unref(propsRef) } as FormProps;
  });

  const getFormClass = computed(() => {
    const { inset, readonlyMode, compact } = unref(getProps);
    return [
      prefixCls,
      {
        [`${prefixCls}--inset`]: inset,
        [`${prefixCls}--readonly`]: readonlyMode,
        [`${prefixCls}--compact`]: compact,
      },
    ];
  });

  // Get uniform row style and Row configuration for the entire form
  const getRow = computed(() => {
    const { baseRowStyle = {}, rowProps } = unref(getProps);
    return {
      style: baseRowStyle,
      ...rowProps,
    };
  });

  const getBindValue = computed(() =>
    omit({ ...attrs, ...props, ...unref(getProps) } as AntFormProps, IGNORE_PROPS_KEYS),
  );

  const renderKeys = shallowRef<Recordable<string>>({});
  const getSchema = computed((): FormSchema[] => {
    const schemas: FormSchema[] = cloneDeep(unref(schemaRef) || (unref(getProps).schemas as any));
    const { readonlyMode, layout, hideLabel } = unref(getProps);
    for (const schema of schemas) {
      const key = renderKeys.value[schema.field];
      if (key) {
        schema['_key'] = key;
      }
      if (readonlyMode) {
        schema.component = getReadonlyComponent(schema.component, schema.componentProps.longText);
        schema.required = false;
      }
      if (layout !== 'inline' && schema.showIn === 'inlineText') {
        Object.assign(schema, {
          colProps: { ...schema.colProps, span: 'unset', class: 'mts-col-inline' },
        });
      }

      const {
        defaultValue,
        component,
        componentProps = {},
        isHandleDateDefaultValue = true,
        field,
        isHandleDefaultValue = true,
        valueFormat,
      } = schema;

      // label支持多语言
      if (schema.label && typeof schema.label === 'string') {
        schema.dataLangKey =
          typeof schema.label === 'string' && t(schema.label) !== schema.label ? schema.label : '';
        schema.label = t(schema.label);
      }
      if (hideLabel) {
        schema.hideLabel = schema.hideLabel ?? true;
      }

      // handle date type
      if (
        isHandleDateDefaultValue &&
        defaultValue &&
        component &&
        dateItemType.includes(component)
      ) {
        const opt = {
          schema,
          formModel,
          formActionType: {} as FormActionType,
        };
        const valueFormat = componentProps
          ? typeof componentProps === 'function'
            ? componentProps(opt)['valueFormat']
            : componentProps['valueFormat']
          : null;
        if (!isArray(defaultValue)) {
          schema.defaultValue = valueFormat
            ? dateUtil(defaultValue).format(valueFormat)
            : dateUtil(defaultValue);
        } else {
          const def: any[] = [];
          defaultValue.forEach((item) => {
            def.push(valueFormat ? dateUtil(item).format(valueFormat) : dateUtil(item));
          });
          schema.defaultValue = def;
        }
      }
      // handle upload type
      if (defaultValue && itemIsUploadComponent(schema?.component)) {
        if (isArray(defaultValue)) {
          schema.defaultValue = defaultValue;
        } else if (typeof defaultValue == 'string') {
          schema.defaultValue = [defaultValue];
        }
      }

      // handle schema.valueFormat
      if (isHandleDefaultValue && defaultValue && component && isFunction(valueFormat)) {
        schema.defaultValue = valueFormat({
          value: defaultValue,
          schema,
          model: formModel,
          field,
        });
      }
    }

    const sortedSchemas = [
      ...schemas.filter((i) => i.showIn === 'inlineText'),
      ...schemas.filter((i) => i.showIn !== 'inlineText'),
    ];
    const lastIndex = findLastIndex(sortedSchemas, { showIn: 'inlineText' });
    if (lastIndex !== -1) {
      sortedSchemas.splice(lastIndex + 1, 0, {
        field: '_BR_',
        componentProps: { showSplit: unref(getProps).inlineTextSplit },
      });
    }
    return sortedSchemas as FormSchema[];
  });

  const { showDropDown } = useDropdown({ formElRef });
  const dropDownOptions = computed(() => {
    const options: DataListItem[] = [];
    for (const i of getSchema.value) {
      let isShow = true;
      if (isBoolean(i.show)) isShow = i.show;
      if (isBoolean(i.ifShow)) isShow = i.ifShow;
      if (isFunction(i.show))
        isShow = i.show({ field: i.field, model: formModel, values: formModel, schema: i });
      if (isFunction(i.ifShow))
        isShow = i.ifShow({ field: i.field, model: formModel, values: formModel, schema: i });

      if (isShow) {
        let value = get(formModel, i.field);
        const { suffix, concatFields, concatBy, class: valueClass } = i.componentProps;
        if (typeof suffix === 'string' && typeof value !== 'undefined') {
          value += replaceUrl(suffix, formModel);
        }
        if (concatFields?.length && value?.every((i) => typeof i === 'object')) {
          const getConcatText = (value: Recordable) =>
            concatText(
              concatFields.map((i: string) => get(value, i)),
              concatBy,
            );
          value = value.map(getConcatText).join(' ; ') || void 0;
        }
        options.push({
          value,
          label: i.label as string,
          langKey: i.dataLangKey,
          valueClass,
          customRender:
            typeof i.render === 'function'
              ? () => i.render!({ field: i.field, model: formModel, values: formModel, schema: i })
              : void 0,
        });
      }
    }
    return options;
  });

  const { handleFormValues, initDefault, updateFormModel } = useFormValues({
    getProps,
    defaultValueRef,
    getSchema,
    formModel,
  });

  const emitChange = (schema: FormSchema) => emit('change', { ...formModel }, schema);

  const formAction = useFormEvents({
    emit,
    getProps,
    formModel,
    getSchema,
    defaultValueRef,
    formElRef: formElRef as Ref<FormActionType>,
    schemaRef: schemaRef as Ref<FormSchema[]>,
    handleFormValues,
    updateFormModel,
    emitChange,
    renderKeys,
  });

  const { handleKeyboardEnter, focusByField, updateFocusFixedInput } = useFormFocus({
    getFormEl: () => formElRef.value?.$el,
    getProps,
    handleSubmit: formAction.submit,
    setFieldsValue: formAction.setFieldsValue,
    emit,
  });

  createFormContext({
    resetAction: formAction.resetFields,
    submitAction: formAction.submit,
  });

  watch(
    () => unref(getProps).model,
    () => {
      const { model } = unref(getProps);
      if (!model) return;
      formAction.setFieldsValue(model);
    },
    {
      immediate: true,
    },
  );

  watch(
    () => props.schemas,
    (schemas) => {
      formAction.resetSchema(schemas ?? []);
    },
  );

  watch(
    () => getSchema.value,
    (schema) => {
      nextTick(() => {
        updateFocusFixedInput();
        //  Solve the problem of modal adaptive height calculation when the form is placed in the modal
        modalFn?.redoModalHeight?.();
      });
      if (unref(isInitedDefaultRef)) {
        return;
      }
      if (schema?.length) {
        initDefault(formAction.updateRelateFields);
        isInitedDefaultRef.value = true;
      }
    },
  );

  async function setProps(formProps: Partial<FormProps>): Promise<void> {
    propsRef.value = deepMerge(unref(propsRef) || {}, formProps);

    if (formProps.schemas?.length && schemaRef.value?.length)
      formAction.resetSchema(formProps.schemas);
  }

  function setFormModel(key: string, value: any, schema: FormSchema) {
    formModel[key] = value;
    emit('field-value-change', key, value, schema);
    emitChange(schema);

    updateFormModel(key, schema);
    formAction.updateRelateFields(key, value, schema);
    // TODO 优化验证，这里如果是autoLink=false手动关联的情况下才会再次触发此函数
    if (schema && schema.itemProps && !schema.itemProps.autoLink) {
      formAction.validateFields([key]).catch((_) => {});
    }
  }

  const formActionType = { ...formAction, setProps, focusByField };
  defineExpose(formActionType);

  onMounted(() => {
    initDefault(formAction.updateRelateFields);
    emit('register', formActionType);
  });
</script>
